import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  BookOpen, 
  Crown, 
  Scroll, 
  Star, 
  Heart, 
  Shield, 
  Zap, 
  Gift,
  Sparkles,
  Award,
  Flame,
  Sun,
  Moon,
  Target,
  Compass,
  Gem,
  Trophy,
  Hash,
  Book,
  Hand,
  FileText,
  ScrollText,
  Star as StarIcon,
  Circle,
  Hash as HashIcon,
  Footprints,
  Bell
} from 'lucide-react';
import { TORAH_PORTIONS, HOLIDAYS, PRAYER_TYPES, getServicesForPrayer, getTorahPortionsForYear } from '../data/torahData';
import { Debt, User, Service } from '../types';
import { getAllServices } from '../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background: #f5f5f5;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding-right: 40px;
`;

const Title = styled.h2`
  margin: 0;
  color: #333;
  font-size: 24px;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const Step = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  background: ${props => 
    props.$active ? '#4CAF50' : 
    props.$completed ? '#8BC34A' : '#E0E0E0'
  };
  color: ${props => props.$active || props.$completed ? 'white' : '#666'};
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  margin-right: 8px;
  font-size: 14px;
`;

const StepArrow = styled.div`
  color: #666;
  margin: 0 4px;
`;

const StepContent = styled.div`
  min-height: 300px;
`;

const CategorySelection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

const CategoryButton = styled.button<{ $selected: boolean }>`
  padding: 20px;
  border: 2px solid ${props => props.$selected ? '#4CAF50' : '#E0E0E0'};
  border-radius: 12px;
  background: ${props => props.$selected ? '#E8F5E8' : 'white'};
  cursor: pointer;
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.$selected ? '#4CAF50' : '#666'};
  transition: all 0.2s;
  
  &:hover {
    border-color: #4CAF50;
    background: #E8F5E8;
  }
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectionItem = styled.button<{ $selected: boolean }>`
  padding: 12px 16px;
  border: 1px solid ${props => props.$selected ? '#4CAF50' : '#E0E0E0'};
  border-radius: 8px;
  background: ${props => props.$selected ? '#E8F5E8' : 'white'};
  cursor: pointer;
  text-align: right;
  font-size: 14px;
  color: ${props => props.$selected ? '#4CAF50' : '#333'};
  transition: all 0.2s;
  
  &:hover {
    border-color: #4CAF50;
    background: #E8F5E8;
  }
`;

const PrayerTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const PrayerTypeButton = styled.button<{ $selected: boolean }>`
  padding: 16px;
  border: 2px solid ${props => props.$selected ? '#4CAF50' : '#E0E0E0'};
  border-radius: 8px;
  background: ${props => props.$selected ? '#E8F5E8' : 'white'};
  cursor: pointer;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  color: ${props => props.$selected ? '#4CAF50' : '#333'};
  transition: all 0.2s;
  
  &:hover {
    border-color: #4CAF50;
    background: #E8F5E8;
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
  max-height: 200px;
  overflow-y: auto;
`;

const ServiceButton = styled.button<{ $selected: boolean }>`
  padding: 12px;
  border: 1px solid ${props => props.$selected ? '#4CAF50' : '#E0E0E0'};
  border-radius: 8px;
  background: ${props => props.$selected ? '#E8F5E8' : 'white'};
  cursor: pointer;
  text-align: right;
  font-size: 13px;
  color: ${props => props.$selected ? '#4CAF50' : '#333'};
  transition: all 0.2s;
  
  &:hover {
    border-color: #4CAF50;
    background: #E8F5E8;
  }
`;

const ServicePrice = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 4px;
  font-style: italic;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: #333;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #E0E0E0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #E0E0E0;
  border-radius: 8px;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #E0E0E0;
  border-radius: 8px;
  font-size: 16px;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
`;

const Button = styled.button<{ $primary?: boolean; $disabled?: boolean }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  background: ${props => 
    props.$disabled ? '#E0E0E0' : 
    props.$primary ? '#4CAF50' : 'white'
  };
  color: ${props => 
    props.$disabled ? '#999' : 
    props.$primary ? 'white' : '#666'
  };
  border: ${props => props.$primary ? 'none' : '2px solid #E0E0E0'};
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: ${props => props.$primary ? '#45A049' : '#F5F5F5'};
  }
`;

const SummaryCard = styled.div`
  background: #F8F9FA;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
    font-weight: bold;
    font-size: 18px;
    color: #4CAF50;
    border-top: 1px solid #E0E0E0;
    padding-top: 8px;
  }
`;

interface AddDebtModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (debt: Partial<Debt>) => void;
  members: User[];
  debtTypes: any[];
}

const AddDebtModal: React.FC<AddDebtModalProps> = ({
  isOpen,
  onClose,
  onSave,
  members,
  debtTypes
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    debtCategory: '' as 'shabbat' | 'holiday' | '',
    torahPortionId: '',
    holidayId: '',
    prayerTypeId: '',
    serviceId: '',
    memberId: '',
    amount: 0,
    description: '',
    year: 5786 // שנה עברית נוכחית
  });

  const [selectedService, setSelectedService] = useState<any>(null);
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [isMemberDropdownOpen, setIsMemberDropdownOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);
  const [services, setServices] = useState<Service[]>([]);

  const steps = [
    'בחירת סוג',
    'בחירת תאריך',
    'בחירת תפילה',
    'בחירת שירות',
    'פרטי החוב'
  ];

  // Load services from database
  useEffect(() => {
    const loadServices = async () => {
      try {
        console.log('Loading services from database...');
        const servicesData = await getAllServices();
        console.log('Loaded services:', servicesData);
        
        if (servicesData.length === 0) {
          console.log('No services found in database, using fallback data');
          // Fallback to static data if database is empty
          const fallbackServices = [
            { id: '1', name: 'פתיחת ההיכל', hebrewName: 'פתיחת ההיכל', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '2', name: 'הגבהת ספר תורה', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '3', name: 'הולכת ספר תורה', hebrewName: 'הולכת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '4', name: 'עליה ראשונה', hebrewName: 'עליה ראשונה', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '5', name: 'עליה שנייה', hebrewName: 'עליה שנייה', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '6', name: 'עליה שלישית', hebrewName: 'עליה שלישית', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '7', name: 'עליה רביעית', hebrewName: 'עליה רביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '8', name: 'עליה חמישית', hebrewName: 'עליה חמישית', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '9', name: 'עליה שישית', hebrewName: 'עליה שישית', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '10', name: 'עליה שביעית', hebrewName: 'עליה שביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '11', name: 'הפטרה', hebrewName: 'הפטרה', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            { id: '11b', name: 'פעמונים לספר תורה', hebrewName: 'פעמונים לספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_early', createdAt: new Date() },
            // שחרית רגיל
            { id: '12', name: 'פתיחת ההיכל', hebrewName: 'פתיחת ההיכל', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '13', name: 'הגבהת ספר תורה', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '14', name: 'הולכת ספר תורה', hebrewName: 'הולכת ספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '15', name: 'עליה ראשונה', hebrewName: 'עליה ראשונה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '16', name: 'עליה שנייה', hebrewName: 'עליה שנייה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '17', name: 'עליה שלישית', hebrewName: 'עליה שלישית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '18', name: 'עליה רביעית', hebrewName: 'עליה רביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '19', name: 'עליה חמישית', hebrewName: 'עליה חמישית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '20', name: 'עליה שישית', hebrewName: 'עליה שישית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '21', name: 'עליה שביעית', hebrewName: 'עליה שביעית', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '22', name: 'הפטרה', hebrewName: 'הפטרה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            { id: '22b', name: 'פעמונים לספר תורה', hebrewName: 'פעמונים לספר תורה', suggestedPrice: 0, prayerTypeId: 'shacharit_regular', createdAt: new Date() },
            // מנחה
            { id: '23', name: 'פתיחת ההיכל', hebrewName: 'פתיחת ההיכל', suggestedPrice: 0, prayerTypeId: 'mincha', createdAt: new Date() },
            { id: '24', name: 'הגבהת ספר תורה', hebrewName: 'הגבהת ספר תורה', suggestedPrice: 0, prayerTypeId: 'mincha', createdAt: new Date() },
            { id: '25', name: 'הולכת ספר תורה', hebrewName: 'הולכת ספר תורה', suggestedPrice: 0, prayerTypeId: 'mincha', createdAt: new Date() },
            { id: '26', name: 'עליה ראשונה', hebrewName: 'עליה ראשונה', suggestedPrice: 0, prayerTypeId: 'mincha', createdAt: new Date() },
            { id: '27', name: 'עליה שנייה', hebrewName: 'עליה שנייה', suggestedPrice: 0, prayerTypeId: 'mincha', createdAt: new Date() },
            { id: '28', name: 'עליה שלישית', hebrewName: 'עליה שלישית', suggestedPrice: 0, prayerTypeId: 'mincha', createdAt: new Date() },
            // אחר
            { id: '29', name: 'שירות כללי', hebrewName: 'שירות כללי', suggestedPrice: 0, prayerTypeId: 'other', createdAt: new Date() }
          ];
          setServices(fallbackServices);
        } else {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    
    if (isOpen) {
      loadServices();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      debtCategory: '',
      torahPortionId: '',
      holidayId: '',
      prayerTypeId: '',
      serviceId: '',
      memberId: '',
      amount: 0,
      description: '',
      year: 5786
    });
    setCurrentStep(1);
    setSelectedService(null);
    setMemberSearchTerm('');
    setSelectedMember(null);
    setIsMemberDropdownOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target as Node)) {
        setIsMemberDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter members based on search term
  const filteredMembers = members
    .filter(member => 
      member.name.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
      member.lastName.toLowerCase().includes(memberSearchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const nameA = `${a.name} ${a.lastName}`.toLowerCase();
      const nameB = `${b.name} ${b.lastName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const handleMemberSelect = (member: User) => {
    setSelectedMember(member);
    setFormData(prev => ({ ...prev, memberId: member.id }));
    setMemberSearchTerm(`${member.name} ${member.lastName}`);
    setIsMemberDropdownOpen(false);
  };

  const handleMemberSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMemberSearchTerm(value);
    setIsMemberDropdownOpen(true);
    
    if (value === '') {
      setSelectedMember(null);
      setFormData(prev => ({ ...prev, memberId: '' }));
    }
  };

  const handleCategorySelect = (category: 'shabbat' | 'holiday') => {
    setFormData(prev => ({ ...prev, debtCategory: category }));
    // Auto advance to next step
    setTimeout(() => setCurrentStep(2), 300);
  };

  const handleTorahPortionSelect = (portionId: string) => {
    setFormData(prev => ({ ...prev, torahPortionId: portionId }));
    // Auto advance to next step
    setTimeout(() => setCurrentStep(3), 300);
  };

  const handleHolidaySelect = (holidayId: string) => {
    setFormData(prev => ({ ...prev, holidayId: holidayId }));
    // Auto advance to next step
    setTimeout(() => setCurrentStep(3), 300);
  };

  const handlePrayerTypeSelect = (prayerTypeId: string) => {
    setFormData(prev => ({ ...prev, prayerTypeId: prayerTypeId }));
    // Auto advance to next step
    setTimeout(() => setCurrentStep(4), 300);
  };

  const handleServiceSelect = (service: any) => {
    setFormData(prev => ({ ...prev, serviceId: service.id, amount: 0 }));
    setSelectedService(service);
    // Auto advance to next step
    setTimeout(() => setCurrentStep(5), 300);
  };

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };


  const getAvailableServices = () => {
    if (!formData.prayerTypeId) {
      console.log('No prayer type selected');
      return [];
    }
    
    console.log('Getting available services for prayer type:', formData.prayerTypeId);
    console.log('All services:', services);
    console.log('Services length:', services.length);
    
    // Filter services from database by prayer type
    const filtered = services.filter(service => service.prayerTypeId === formData.prayerTypeId);
    console.log('Filtered services:', filtered);
    console.log('Filtered length:', filtered.length);
    
    // Sort by Hebrew name alphabetically
    return filtered.sort((a, b) => a.hebrewName.localeCompare(b.hebrewName, 'he'));
  };

  const getSelectedDate = () => {
    if (formData.debtCategory === 'shabbat' && formData.torahPortionId) {
      const portion = TORAH_PORTIONS.find(p => p.id === formData.torahPortionId);
      return portion ? { hebrew: portion.hebrewDate, gregorian: portion.gregorianDate } : null;
    }
    if (formData.debtCategory === 'holiday' && formData.holidayId) {
      const holiday = HOLIDAYS.find(h => h.id === formData.holidayId);
      return holiday ? { hebrew: holiday.hebrewDate, gregorian: holiday.gregorianDate } : null;
    }
    return null;
  };

  const getHebrewYear = (year: number) => {
    const yearStr = year.toString();
    const lastTwoDigits = parseInt(yearStr.slice(-2));
    
    // המרת שתי הספרות האחרונות לאותיות עבריות
    const tens = Math.floor(lastTwoDigits / 10);
    const ones = lastTwoDigits % 10;
    
    let hebrewYear = '';
    
    // עשרות
    if (tens === 1) {
      hebrewYear += 'י';
    } else if (tens === 2) {
      hebrewYear += 'כ';
    } else if (tens === 3) {
      hebrewYear += 'ל';
    } else if (tens === 4) {
      hebrewYear += 'מ';
    } else if (tens === 5) {
      hebrewYear += 'נ';
    } else if (tens === 6) {
      hebrewYear += 'ס';
    } else if (tens === 7) {
      hebrewYear += 'ע';
    } else if (tens === 8) {
      hebrewYear += 'פ';
    } else if (tens === 9) {
      hebrewYear += 'צ';
    }
    
    // יחידות
    if (ones === 1) {
      hebrewYear += 'א';
    } else if (ones === 2) {
      hebrewYear += 'ב';
    } else if (ones === 3) {
      hebrewYear += 'ג';
    } else if (ones === 4) {
      hebrewYear += 'ד';
    } else if (ones === 5) {
      hebrewYear += 'ה';
    } else if (ones === 6) {
      hebrewYear += 'ו';
    } else if (ones === 7) {
      hebrewYear += 'ז';
    } else if (ones === 8) {
      hebrewYear += 'ח';
    } else if (ones === 9) {
      hebrewYear += 'ט';
    }
    
    return hebrewYear || yearStr;
  };

  const getServiceIcon = (serviceName: string) => {
    const name = serviceName.toLowerCase();
    
    if (name.includes('פתיחת') || name.includes('היכל')) {
      return <BookOpen size={20} />; // דלת פתוחה
    } else if (name.includes('פעמונים')) {
      return <Bell size={20} />; // פעמון
    } else if (name.includes('הגבהת')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        <Hand size={16} />
        <Hand size={16} />
      </div>; // שתי ידיים
    } else if (name.includes('הולכת')) {
      return <Footprints size={20} />; // רגליים
    } else if (name.includes('עליה') && name.includes('ראשונה')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Circle size={16} />
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>1</span>
      </div>; // עיגול עם 1
    } else if (name.includes('עליה') && name.includes('שנייה')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Circle size={16} />
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>2</span>
      </div>; // עיגול עם 2
    } else if (name.includes('עליה') && name.includes('שלישית')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Circle size={16} />
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>3</span>
      </div>; // עיגול עם 3
    } else if (name.includes('עליה') && name.includes('רביעית')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Circle size={16} />
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>4</span>
      </div>; // עיגול עם 4
    } else if (name.includes('עליה') && name.includes('חמישית')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Circle size={16} />
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>5</span>
      </div>; // עיגול עם 5
    } else if (name.includes('עליה') && name.includes('שישית')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Circle size={16} />
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>6</span>
      </div>; // עיגול עם 6
    } else if (name.includes('עליה') && name.includes('שביעית')) {
      return <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Circle size={16} />
        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#667eea' }}>7</span>
      </div>; // עיגול עם 7
    } else if (name.includes('הפטרה')) {
      return <FileText size={20} />; // מגילה/טקסט
    } else if (name.includes('כללי') || name.includes('שירות')) {
      return <Gem size={20} />; // יהלום
    } else {
      return <Trophy size={20} />; // גביע
    }
  };

  const handleSave = () => {
    const selectedDate = getSelectedDate();
    if (!selectedDate || !formData.debtCategory) return;

    const debtData: Partial<Debt> = {
      memberId: formData.memberId,
      debtTypeId: formData.serviceId, // Using service as debt type
      amount: formData.amount,
      description: formData.description,
      debtCategory: formData.debtCategory as 'shabbat' | 'holiday',
      prayerTypeId: formData.prayerTypeId,
      serviceId: formData.serviceId,
      hebrewDate: selectedDate.hebrew,
      gregorianDate: selectedDate.gregorian,
      isPaid: false,
      paidAmount: 0,
      remainingAmount: formData.amount,
      payments: []
    };

    // Only add torahPortionId or holidayId if they have values
    if (formData.torahPortionId) {
      debtData.torahPortionId = formData.torahPortionId;
    }
    if (formData.holidayId) {
      debtData.holidayId = formData.holidayId;
    }

    onSave(debtData);
    onClose();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.debtCategory !== '';
      case 2: return formData.debtCategory === 'shabbat' ? formData.torahPortionId !== '' : formData.holidayId !== '';
      case 3: return formData.prayerTypeId !== '';
      case 4: return formData.serviceId !== '';
      case 5: return selectedMember !== null && formData.amount > 0;
      default: return false;
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={24} />
        </CloseButton>
        
        <Header>
          <Title>הוספת חוב חדש</Title>
        </Header>

        <StepIndicator>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Step 
                $active={currentStep === index + 1} 
                $completed={currentStep > index + 1}
              >
                {step}
              </Step>
              {index < steps.length - 1 && <StepArrow>→</StepArrow>}
            </React.Fragment>
          ))}
        </StepIndicator>

        <StepContent>
          {currentStep === 1 && (
            <div>
              <h3 style={{ marginBottom: '20px', textAlign: 'right' }}>בחר סוג חוב</h3>
              <CategorySelection>
                <CategoryButton
                  $selected={formData.debtCategory === 'shabbat'}
                  onClick={() => handleCategorySelect('shabbat')}
                >
                  שבת
                </CategoryButton>
                <CategoryButton
                  $selected={formData.debtCategory === 'holiday'}
                  onClick={() => handleCategorySelect('holiday')}
                >
                  חגים
                </CategoryButton>
              </CategorySelection>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h3 style={{ marginBottom: '20px', textAlign: 'right' }}>
                {formData.debtCategory === 'shabbat' ? 'בחר פרשה' : 'בחר חג'}
              </h3>
              
              <FormGroup>
                <Label>שנה עברית</Label>
                <Select
                  value={formData.year}
                  onChange={(e) => {
                    const year = parseInt(e.target.value);
                    setFormData(prev => ({ ...prev, year, torahPortionId: '', holidayId: '' }));
                  }}
                >
                  <option value={5786}>תשפ"ו (2025-2026)</option>
                  <option value={5787}>תשפ"ז (2026-2027)</option>
                  <option value={5788}>תשפ"ח (2027-2028)</option>
                  <option value={5789}>תשפ"ט (2028-2029)</option>
                  <option value={5790}>תש"ץ (2029-2030)</option>
                </Select>
              </FormGroup>

              <SelectionGrid>
                {formData.debtCategory === 'shabbat' 
                  ? getTorahPortionsForYear(formData.year).map(portion => (
                      <SelectionItem
                        key={portion.id}
                        $selected={formData.torahPortionId === portion.id}
                        onClick={() => handleTorahPortionSelect(portion.id)}
                      >
                        <div>{portion.hebrewName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{portion.hebrewDate}</div>
                      </SelectionItem>
                    ))
                  : HOLIDAYS.map(holiday => (
                      <SelectionItem
                        key={holiday.id}
                        $selected={formData.holidayId === holiday.id}
                        onClick={() => handleHolidaySelect(holiday.id)}
                      >
                        <div>{holiday.hebrewName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{holiday.hebrewDate}</div>
                      </SelectionItem>
                    ))
                }
              </SelectionGrid>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h3 style={{ marginBottom: '20px', textAlign: 'right' }}>בחר תפילה</h3>
              <PrayerTypeGrid>
                {PRAYER_TYPES.map(prayer => (
                  <PrayerTypeButton
                    key={prayer.id}
                    $selected={formData.prayerTypeId === prayer.id}
                    onClick={() => handlePrayerTypeSelect(prayer.id)}
                  >
                    {prayer.hebrewName}
                  </PrayerTypeButton>
                ))}
              </PrayerTypeGrid>
            </div>
          )}

          {currentStep === 4 && (
            <div>
              <h3 style={{ marginBottom: '20px', textAlign: 'right' }}>בחר שירות</h3>
              {(() => {
                const availableServices = getAvailableServices();
                console.log('Rendering step 4, available services:', availableServices);
                return (
                  <ServiceGrid>
                    {availableServices.map(service => (
                      <ServiceButton
                        key={service.id}
                        $selected={formData.serviceId === service.id}
                        onClick={() => handleServiceSelect(service)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getServiceIcon(service.hebrewName)}
                          <span>{service.hebrewName}</span>
                        </div>
                      </ServiceButton>
                    ))}
                    {availableServices.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        לא נמצאו שירותים עבור סוג התפילה הנבחר
                      </div>
                    )}
                  </ServiceGrid>
                );
              })()}
            </div>
          )}

          {currentStep === 5 && (
            <div>
              <h3 style={{ marginBottom: '20px', textAlign: 'right' }}>פרטי החוב</h3>
              
              <SummaryCard>
                <h4 style={{ margin: '0 0 12px 0', textAlign: 'right' }}>סיכום החוב</h4>
                <SummaryRow>
                  <span>סוג:</span>
                  <span>{formData.debtCategory === 'shabbat' ? 'שבת' : 'חג'}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>תאריך:</span>
                  <span>{getSelectedDate()?.hebrew} (תש"פ{getHebrewYear(formData.year)})</span>
                </SummaryRow>
                <SummaryRow>
                  <span>תפילה:</span>
                  <span>{PRAYER_TYPES.find(p => p.id === formData.prayerTypeId)?.hebrewName}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>שירות:</span>
                  <span>{selectedService?.hebrewName}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>מתפלל:</span>
                  <span>{selectedMember ? `${selectedMember.name} ${selectedMember.lastName}` : 'לא נבחר'}</span>
                </SummaryRow>
                <SummaryRow>
                  <span>סכום:</span>
                  <span>₪{formData.amount}</span>
                </SummaryRow>
              </SummaryCard>

              <FormGroup>
                <Label>מתפלל</Label>
                <div style={{ position: 'relative' }} ref={memberDropdownRef}>
                  <Input
                    type="text"
                    value={memberSearchTerm}
                    onChange={handleMemberSearchChange}
                    onFocus={() => setIsMemberDropdownOpen(true)}
                    placeholder="חפש מתפלל..."
                    style={{ border: '2px solid #E0E0E0', borderRadius: '8px' }}
                  />
                  {isMemberDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map(member => (
                          <div
                            key={member.id}
                            onClick={() => handleMemberSelect(member)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              textAlign: 'right'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'white';
                            }}
                          >
                            {member.name} {member.lastName}
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '12px 16px', color: '#666', textAlign: 'center' }}>
                          לא נמצאו מתפללים
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </FormGroup>

              <FormGroup>
                <Label>סכום (₪)</Label>
                <Input
                  type="text"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
                  placeholder={selectedService ? `הצעה: ₪${selectedService.suggestedPrice}` : 'הזן סכום'}
                />
                {selectedService && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    מחיר מוצע: ₪{selectedService.suggestedPrice}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>תיאור נוסף (אופציונלי)</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="הוסף תיאור נוסף..."
                />
              </FormGroup>
            </div>
          )}
        </StepContent>

        <ButtonGroup>
          <Button onClick={handleBack} $disabled={currentStep === 1}>
            <ChevronLeft size={20} style={{ marginLeft: '8px' }} />
            חזור
          </Button>
          
          {currentStep < steps.length ? (
            <Button 
              onClick={handleNext} 
              $primary 
              $disabled={!canProceed()}
            >
              הבא
              <ChevronRight size={20} style={{ marginRight: '8px' }} />
            </Button>
          ) : (
            <Button 
              onClick={handleSave} 
              $primary 
              $disabled={!canProceed()}
            >
              שמור חוב
            </Button>
          )}
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddDebtModal;
